import { useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'

import { Button, Field, Input, Note } from '../components/ui'
import { api } from '../lib/api'
import { AuthLayout } from './AuthLayout'

const PASSWORD_MIN = 12

/** Ask for a reset link. **The response is identical** whether or not the
 *  address exists — the form must not become a user-enumeration oracle. */
export function ForgotPassword() {
  const [email, setEmail] = useState('')
  const [done, setDone] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  async function submit(event: React.FormEvent) {
    event.preventDefault()
    setBusy(true)
    try {
      const result = await api.forgotPassword(email)
      setDone(result.detail)
    } catch {
      // Even a failure shows the same message: an error here would distinguish
      // a known address from an unknown one.
      setDone('If That Address Has an Account, a Reset Link Is on Its Way.')
    } finally {
      setBusy(false)
    }
  }

  if (done) {
    return (
      <AuthLayout title="Check Your Email">
        <Note tone="good">{done}</Note>
        <p className="mt-4 text-sm">
          <Link to="/login" style={{ color: 'var(--brand-ink)' }}>Back to Sign In</Link>
        </p>
      </AuthLayout>
    )
  }

  return (
    <AuthLayout title="Reset Your Password">
      <form onSubmit={submit} noValidate>
        <Field label="Email">
          <Input type="email" value={email} required autoComplete="username"
                 onChange={(event) => setEmail(event.target.value)} />
        </Field>
        <Button type="submit" variant="primary" disabled={busy} className="w-full">
          {busy ? 'Sending…' : 'Send a Reset Link'}
        </Button>
      </form>
      <p className="mt-4 text-sm">
        <Link to="/login" style={{ color: 'var(--brand-ink)' }}>Back to Sign In</Link>
      </p>
    </AuthLayout>
  )
}

/**
 * Set a password from a single-use link.
 *
 * One screen serves three flows — reset, invite, and the bootstrap admin —
 * because they are one mechanism. `mode` only changes the wording.
 */
export function SetPassword({ mode }: { mode: 'reset' | 'invite' }) {
  const [params] = useSearchParams()
  const token = params.get('token') ?? ''
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [done, setDone] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  async function submit(event: React.FormEvent) {
    event.preventDefault()
    if (password !== confirm) {
      setError('The Two Passwords Do Not Match.')
      return
    }
    setBusy(true)
    setError(null)
    try {
      if (mode === 'reset') await api.resetPassword(token, password)
      else await api.setPassword(token, password)
      setDone(true)
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could Not Set the Password')
    } finally {
      setBusy(false)
    }
  }

  if (!token) {
    return (
      <AuthLayout title="That Link Is Incomplete">
        <Note tone="error">
          The Link Is Missing Its Token. Ask an Administrator to Send a New One.
        </Note>
        <p className="mt-4 text-sm">
          <Link to="/login" style={{ color: 'var(--brand-ink)' }}>Back to Sign In</Link>
        </p>
      </AuthLayout>
    )
  }

  if (done) {
    return (
      <AuthLayout title="Password Set">
        <Note tone="good">
          Your Password Is Set. Any Other Sessions Have Been Signed Out.
        </Note>
        <p className="mt-4 text-sm">
          <Link to="/login" style={{ color: 'var(--brand-ink)' }}>Sign in</Link>
        </p>
      </AuthLayout>
    )
  }

  return (
    <AuthLayout title={mode === 'reset' ? 'Choose a New Password' : 'Set Your Password'}>
      {error && <Note tone="error">{error}</Note>}
      <form onSubmit={submit} noValidate>
        <Field label="New Password" hint={`At least ${PASSWORD_MIN} characters.`}>
          <Input type="password" value={password} required minLength={PASSWORD_MIN}
                 autoComplete="new-password"
                 onChange={(event) => setPassword(event.target.value)} />
        </Field>
        <Field label="Confirm Password">
          <Input type="password" value={confirm} required minLength={PASSWORD_MIN}
                 autoComplete="new-password"
                 onChange={(event) => setConfirm(event.target.value)} />
        </Field>
        <Button type="submit" variant="primary" disabled={busy} className="w-full">
          {busy ? 'Saving…' : 'Set Password'}
        </Button>
      </form>
    </AuthLayout>
  )
}

/** Confirm an address. Verification comes **before** approval so an Admin
 *  cannot approve a typo into an account nobody can reach. */
export function VerifyEmail() {
  const [params] = useSearchParams()
  const token = params.get('token') ?? ''
  const [state, setState] = useState<'working' | 'ok' | 'failed'>('working')

  useEffect(() => {
    if (!token) {
      setState('failed')
      return
    }
    api
      .verifyEmail(token)
      .then(() => setState('ok'))
      .catch(() => setState('failed'))
  }, [token])

  return (
    <AuthLayout title="Confirm Your Email">
      {state === 'working' && (
        <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>Confirming…</p>
      )}
      {state === 'ok' && (
        <Note tone="good">
          Address Confirmed. An Administrator Will Review Your Account. You Will
          Not Be Able to Sign in Until It Is Approved.
        </Note>
      )}
      {state === 'failed' && (
        <Note tone="error">
          That Confirmation Link Is Invalid or Has Expired. Ask an Administrator
          to Send a New One.
        </Note>
      )}
      <p className="mt-4 text-sm">
        <Link to="/login" style={{ color: 'var(--brand-ink)' }}>Back to Sign In</Link>
      </p>
    </AuthLayout>
  )
}
